/**
 * Mapeamento dos erros mais comuns de sincronização de cargas/rotinas.
 *
 * Cada item tenta casar tanto pelo código SQLSTATE (ex.: SQLSTATE[23505])
 * quanto por padrões de texto presentes na mensagem de retorno, cobrindo
 * PostgreSQL, Oracle (ORA-) e SQL Server. A ordem importa: o primeiro
 * que casar é o retornado.
 */
const MAPA_ERROS = [
  {
    codigos: ['23505'],
    padroes: [
      /duplicate key value/i,
      /violates unique constraint/i,
      /unique constraint/i,
      /chave duplicada/i,
      /ORA-00001/i,
      /Violation of (UNIQUE|PRIMARY) KEY/i,
      /Cannot insert duplicate key/i,
    ],
    titulo: 'Registro duplicado (chave única / ID repetido)',
    descricao:
      'A carga tentou gravar um ID/chave que já existe no destino. Verifique se a origem está retornando registros duplicados ou se a coluna usada como ID não é única.',
  },
  {
    codigos: ['23502'],
    padroes: [
      /null value in column/i,
      /violates not-null constraint/i,
      /not-null constraint/i,
      /cannot be null/i,
      /ORA-01400/i,
      /Cannot insert the value NULL/i,
    ],
    titulo: 'Campo obrigatório vazio (NOT NULL)',
    descricao:
      'Um campo que não aceita valor nulo veio sem valor da origem. Ajuste a view/consulta para sempre preencher esse campo.',
  },
  {
    codigos: ['23503'],
    padroes: [
      /violates foreign key constraint/i,
      /foreign key constraint/i,
      /ORA-02291/i,
      /ORA-02292/i,
      /conflicted with the (FOREIGN KEY|REFERENCE) constraint/i,
    ],
    titulo: 'Violação de chave estrangeira',
    descricao:
      'O registro referencia um dado que ainda não existe na tabela relacionada. Sincronize antes a tabela referenciada (a "pai").',
  },
  {
    codigos: ['23514'],
    padroes: [/violates check constraint/i, /check constraint/i, /ORA-02290/i],
    titulo: 'Violação de regra de validação (CHECK)',
    descricao:
      'Um valor não atende a uma regra de validação definida na tabela de destino.',
  },
  {
    codigos: ['42501'],
    padroes: [/permission denied/i, /insufficient privilege/i, /permissão negada/i, /ORA-01031/i],
    titulo: 'Permissão negada no banco de dados',
    descricao:
      'O usuário de integração não tem permissão para ler/gravar na tabela ou view envolvida. Revise os GRANTs do usuário no banco de origem.',
  },
  {
    codigos: ['42P01'],
    padroes: [
      /relation ".*" does not exist/i,
      /undefined table/i,
      /ORA-00942/i,
      /Invalid object name/i,
    ],
    titulo: 'Tabela ou view não encontrada',
    descricao:
      'A tabela/view consultada não existe no banco de origem. Confira o nome do objeto na consulta da carga.',
  },
  {
    codigos: ['42703'],
    padroes: [
      /column ".*" does not exist/i,
      /undefined column/i,
      /Invalid column name/i,
      /ORA-00904/i,
    ],
    titulo: 'Coluna não encontrada',
    descricao:
      'Uma coluna referenciada na consulta não existe na origem. Verifique se o nome da coluna está correto.',
  },
  {
    codigos: ['42601', '42000'],
    padroes: [/syntax error at or near/i, /Incorrect syntax near/i, /ORA-00936/i, /ORA-00933/i],
    titulo: 'Erro de sintaxe no SQL da carga',
    descricao:
      'O SQL configurado para a carga está com erro de sintaxe. Revise a consulta.',
  },
  {
    codigos: ['22001'],
    padroes: [
      /value too long for type/i,
      /right truncation/i,
      /String or binary data would be truncated/i,
      /ORA-12899/i,
    ],
    titulo: 'Valor maior que o campo comporta',
    descricao:
      'Um texto excedeu o tamanho máximo da coluna de destino. Aumente a coluna no destino ou trate/limite o valor na origem.',
  },
  {
    codigos: ['22003'],
    padroes: [/numeric.*out of range/i, /numeric value out of range/i, /numeric overflow/i, /ORA-01438/i, /Arithmetic overflow/i],
    titulo: 'Valor numérico fora do limite',
    descricao:
      'Um número excedeu a precisão/escala definida na coluna de destino.',
  },
  {
    codigos: ['22P02', '22007', '22008', '22018'],
    padroes: [
      /invalid input syntax/i,
      /invalid text representation/i,
      /date\/time field value out of range/i,
      /Conversion failed when converting/i,
      /ORA-01858/i,
      /ORA-01722/i,
    ],
    titulo: 'Tipo de dado inválido na conversão',
    descricao:
      'Um valor não pôde ser convertido para o tipo da coluna (ex.: texto em campo numérico ou data inválida).',
  },
  {
    codigos: ['08006', '08001', '08003', '08004', '08S01', 'HY000'],
    padroes: [
      /could not connect/i,
      /connection refused/i,
      /server closed the connection/i,
      /No connection could be made/i,
      /Connection timed out/i,
      /ORA-12541/i,
      /ORA-12170/i,
      /Login timeout expired/i,
    ],
    titulo: 'Falha de conexão com o banco de origem',
    descricao:
      'Não foi possível conectar ao banco de dados da origem. Verifique se o serviço está no ar, o host/porta e a rede/VPN.',
  },
  {
    codigos: ['57014', '57P01'],
    padroes: [
      /canceling statement due to statement timeout/i,
      /Query (execution )?was canceled/i,
      /tempo de execução excedido/i,
      /ORA-01013/i,
    ],
    titulo: 'Tempo de execução excedido (timeout)',
    descricao:
      'A consulta demorou mais que o limite permitido. Considere um modo de carga em lote ou otimizar a consulta/índices.',
  },
  {
    codigos: ['40P01', '40001'],
    padroes: [/deadlock detected/i, /serialization failure/i, /ORA-00060/i, /deadlock/i],
    titulo: 'Conflito de concorrência (deadlock)',
    descricao:
      'A operação conflitou com outra execução simultânea. Costuma resolver tentando sincronizar novamente.',
  },
  {
    codigos: ['53300'],
    padroes: [/too many connections/i, /sorry, too many clients already/i, /ORA-00020/i],
    titulo: 'Muitas conexões abertas no banco',
    descricao:
      'O banco atingiu o limite de conexões simultâneas. Aguarde alguns instantes e tente novamente.',
  },
  {
    codigos: ['53100'],
    padroes: [/could not extend|no space left on device/i, /disk full/i],
    titulo: 'Sem espaço em disco no banco',
    descricao: 'O banco de dados ficou sem espaço em disco para concluir a operação.',
  },
];

/**
 * Interpreta uma mensagem de retorno de sincronização e devolve uma
 * descrição amigável do erro mais provável.
 *
 * @param {string} mensagem Mensagem bruta de retorno (mensagem_retorno / msg)
 * @returns {{ titulo: string, descricao: string, codigo: (string|null) } | null}
 *          Objeto com a explicação, ou null quando não há erro reconhecido
 *          (mensagens de sucesso ou desconhecidas).
 */
export function interpretarErroSincronizacao(mensagem) {
  if (!mensagem || typeof mensagem !== 'string') {
    return null;
  }

  const matchSqlState = mensagem.match(/SQLSTATE\[([0-9A-Za-z]+)\]/);
  const sqlState = matchSqlState ? matchSqlState[1] : null;

  for (const erro of MAPA_ERROS) {
    const casaCodigo = sqlState && erro.codigos.includes(sqlState);
    const casaPadrao = erro.padroes.some((regex) => regex.test(mensagem));

    if (casaCodigo || casaPadrao) {
      return {
        titulo: erro.titulo,
        descricao: erro.descricao,
        codigo: sqlState,
      };
    }
  }

  return null;
}

export default interpretarErroSincronizacao;
